export const handleUpload = async (
  presignedUrl: string,
  file: File | undefined,
  onProgress?: (progress: number) => void,
  presignedFields?: Record<string, string>
) => {
  if (!file || !presignedUrl) {
    //no file or no presigned url
    return;
  }

  // Parse URL to check if it's POST or PUT
  const url = new URL(presignedUrl);
  const hasFields = Boolean(
    presignedFields && Object.keys(presignedFields).length
  );
  const isPostMethod =
    hasFields ||
    url.searchParams.has("policy") ||
    presignedUrl.includes("AWSAccessKeyId");

  // Check if this is a GET presigned URL (for download, not upload)
  const xId = url.searchParams.get("x-id");
  if (xId === "GetObject") {
    throw new Error(
      "This appears to be a presigned URL for downloading (GetObject), not uploading. You need a presigned URL for PutObject or a POST policy for uploading files."
    );
  }

  const resolvedContentType =
    presignedFields?.["Content-Type"] ||
    presignedFields?.["content-type"] ||
    file.type ||
    "application/octet-stream";
  const resolvedChecksum =
    presignedFields?.["x-amz-checksum-sha256"] ||
    presignedFields?.["X-Amz-Checksum-Sha256"] ||
    (await getSha256Base64(file));
  const fileChecksum = isPostMethod ? "" : resolvedChecksum;

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Set up progress tracking
    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status === 200 || xhr.status === 204) {
        if (onProgress) onProgress(100); // Ensure we reach 100%
        resolve();
      } else {
        reject(
          new Error(
            `Upload failed with status: ${xhr.status} - ${xhr.responseText}`
          )
        );
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Network error occurred during upload"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Upload was aborted"));
    });

    if (isPostMethod) {
      // Handle POST presigned URL (with form data)
      const formData = new FormData();

      if (hasFields && presignedFields) {
        Object.entries(presignedFields).forEach(([key, value]) => {
          formData.append(key, value);
        });
      } else {
        // Extract fields from URL parameters for POST
        const urlParams = url.searchParams;

        // Add common form fields if they exist in the URL
        if (urlParams.has("key"))
          formData.append("key", urlParams.get("key") as string);
        if (urlParams.has("AWSAccessKeyId"))
          formData.append(
            "AWSAccessKeyId",
            urlParams.get("AWSAccessKeyId") as string
          );
        if (urlParams.has("policy"))
          formData.append("policy", urlParams.get("policy") as string);
        if (urlParams.has("signature"))
          formData.append("signature", urlParams.get("signature") as string);
        if (urlParams.has("acl"))
          formData.append("acl", urlParams.get("acl") as string);

        // AWS Signature V4 fields
        if (urlParams.has("X-Amz-Algorithm"))
          formData.append(
            "X-Amz-Algorithm",
            urlParams.get("X-Amz-Algorithm") as string
          );
        if (urlParams.has("X-Amz-Credential"))
          formData.append(
            "X-Amz-Credential",
            urlParams.get("X-Amz-Credential") as string
          );
        if (urlParams.has("X-Amz-Date"))
          formData.append("X-Amz-Date", urlParams.get("X-Amz-Date") as string);
        if (urlParams.has("X-Amz-Signature"))
          formData.append(
            "X-Amz-Signature",
            urlParams.get("X-Amz-Signature") as string
          );
      }

      if (
        !presignedFields ||
        (!("Content-Type" in presignedFields) &&
          !("content-type" in presignedFields))
      ) {
        // Content-Type should match what's expected
        formData.append("Content-Type", resolvedContentType);
      }

      if (
        !presignedFields ||
        (!("x-amz-checksum-sha256" in presignedFields) &&
          !("X-Amz-Checksum-Sha256" in presignedFields))
      ) {
        formData.append("x-amz-checksum-sha256", resolvedChecksum);
      }

      // File must be last
      formData.append("file", file);

      // Use base URL without query params for POST
      const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;

      xhr.open("POST", baseUrl);
      xhr.send(formData);
    } else {
      // Handle PUT presigned URL (direct upload)

      // Extract the signed headers from the URL
      const signedHeadersMatch = presignedUrl.match(
        /X-Amz-SignedHeaders=([^&]+)/
      );
      const signedHeaders = signedHeadersMatch
        ? decodeURIComponent(signedHeadersMatch[1]).split(";")
        : [];

      // Check if the URL contains a checksum parameter
      const urlChecksum = url.searchParams.get("X-Amz-Checksum-Sha256");
      if (urlChecksum && urlChecksum !== fileChecksum) {
        throw new Error(
          `File checksum mismatch! Expected: ${urlChecksum}, Got: ${fileChecksum}`
        );
      }

      // Only include headers that are in the signed headers list
      const headers: Record<string, string> = {};

      // IMPORTANT: Fill headers based on actual file data to match what was used during signing

      // Add Content-Type if it's in signed headers - use actual file content type
      if (signedHeaders.includes("content-type")) {
        // console.log(
        //   "Content-Type is in signed headers - setting to file content type"
        // );

        // Use the actual file's content type (this is what your Go code should sign with)
        const fileContentType = file.type || "application/octet-stream";
        headers["Content-Type"] = fileContentType;
      }

      // Add Content-Length if it's in signed headers - use actual file size
      if (signedHeaders.includes("content-length")) {
        // console.log(
        //   "Content-Length is in signed headers - setting to file size"
        // );

        // Use the actual file's size (this is what your Go code should sign with)
        const fileSize = file.size;
        headers["Content-Length"] = fileSize.toString();
      }

      // Add Content-SHA256 checksum if it's in signed headers
      if (signedHeaders.includes("x-amz-content-sha256")) {
        headers["x-amz-content-sha256"] = fileChecksum;
      }
      // Add required meta headers with exact values used during signing
      if (signedHeaders.includes("x-amz-meta-max-content-length")) {
        // Your Go code sets this to "10485760" (10MB)
        headers["x-amz-meta-max-content-length"] = "10485760";
      }

      // Add actual file size as metadata if it's in signed headers
      if (signedHeaders.includes("x-amz-meta-actual-content-length")) {
        headers["x-amz-meta-actual-content-length"] = file.size.toString();
      }

      // Add any other x-amz-meta headers that might be required
      signedHeaders.forEach((header) => {
        if (
          header.startsWith("x-amz-meta-") &&
          header !== "x-amz-meta-max-content-length"
        ) {
          headers[header] = ""; // Default to empty value unless we know the specific value
        }
      });

      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.open("PUT", presignedUrl);
      xhr.send(file);
    }
  });
};

// Calculate SHA-256 checksum for validation
export const getSha256Hex = async (file: File) => {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((str) => str.toString(16).padStart(2, "0"))
    .join("");
};

export const getSha256Base64 = async (file: File) => {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
  const bytes = new Uint8Array(hashBuffer);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};
